import { useEffect, useMemo, useState } from 'react';
import useResponsive from '@/common/hooks/useResponsive';
import useSheetHistory from '@/common/hooks/useSheetHistory';
import { DsDrawer } from '@/modules/doctorHome/components/DsDrawer';
import { splunkInstance } from '@/common/services/splunk';
import {
  type CheraghFacilityCategory,
  type CheraghFacilitiesPublicData,
} from '@/modules/profile/utils/cheraghFacilitiesProfile';

/** حداکثر چیپ بیرون‌رفته (بدون کلیک) در کارت آدرس — سرریز با «+N» به sheet می‌رود. */
const MAX_INLINE_ITEMS = 6;

const CHEVRON_SVG = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M15 6l-6 6 6 6" />
  </svg>
);

const faNum = (value: number | string): string =>
  String(value).replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)]);

type Props = {
  /** دادهٔ عمومی بیکو — همان قرارداد شکل چراغ (general/access/amenities/other/note). */
  data: CheraghFacilitiesPublicData;
  centerId: string | number;
  doctorName?: string;
  profileSlug?: string;
};

const CHIP_CLASS =
  'inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-800';

/**
 * نمایش امکانات مطب در کارت آدرس پروفایل — جایگزین ویجت پلاسمیک چراغ.
 * موارد دسترسی (تصمیم‌ساز برای مراجعه) اولِ ردیف و بدون کلیک دیده می‌شوند؛
 * لیست کامل و نکات در sheet (موبایل) / پنل (دسکتاپ) با DsDrawer.
 */
export const OfficeFacilitiesWidget = ({ data, centerId, doctorName = '', profileSlug = '' }: Props) => {
  const [open, setOpen] = useState(false);
  const { isMobile } = useResponsive();
  useSheetHistory(open && isMobile, () => setOpen(false), 'facilities');

  const general = data.general ?? [];
  const notes = data.note ?? [];
  const inline = useMemo(() => general.slice(0, MAX_INLINE_ITEMS), [general]);
  const restCount = Math.max(0, general.length - inline.length);
  const groups = useMemo(
    () =>
      [data.access, data.amenities, data.other].filter(
        (group): group is CheraghFacilityCategory => Boolean(group?.items?.length),
      ),
    [data.access, data.amenities, data.other],
  );
  const hasSheetContent = groups.length > 0 || notes.length > 0;
  const showMoreLink = hasSheetContent && (restCount > 0 || groups.length > 1 || notes.length > 0);

  const doctorData = { name: doctorName, profile_slug: profileSlug };
  const track = (eventType: 'view' | 'click', clickElement?: string) => {
    splunkInstance('doctor-profile').sendEvent({
      group: 'office-facilities',
      type: eventType,
      event: {
        data: {
          center_id: String(centerId),
          ...(clickElement ? { click_element: clickElement } : {}),
          doctor_data: doctorData,
        },
      },
    });
  };

  useEffect(() => {
    track('view');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [centerId]);

  const openSheet = (clickElement: string) => {
    track('click', clickElement);
    setOpen(true);
  };

  if (general.length === 0 && notes.length === 0) return null;

  return (
    <div className="flex w-full flex-col gap-2" data-testid="office-facilities-widget">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-slate-800">دسترسی‌ها و امکانات</h3>
        {showMoreLink ? (
          <button
            type="button"
            className="inline-flex min-h-8 items-center gap-0.5 px-1 text-xs font-semibold text-[#007AFF]"
            onClick={() => openSheet('more-link')}
          >
            همهٔ امکانات {CHEVRON_SVG}
          </button>
        ) : null}
      </div>

      {inline.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {inline.map((item) => (
            <span key={item.key ?? item.name} className={CHIP_CLASS}>
              {item.icon ? (
                <img src={item.icon} alt="" loading="lazy" className="h-[17px] w-[17px]" />
              ) : null}
              {item.name}
            </span>
          ))}
          {restCount > 0 ? (
            <button
              type="button"
              className="inline-flex min-h-8 items-center gap-1 rounded-lg border border-dashed border-slate-300 px-2.5 py-1.5 text-xs text-slate-500"
              onClick={() => openSheet('chip-more')}
            >
              +{faNum(restCount)} مورد دیگر
            </button>
          ) : null}
        </div>
      ) : null}

      {general.length === 0 && notes.length > 0 ? (
        <ul className="list-disc pr-4 text-xs leading-6 text-slate-700">
          {notes.map((note, index) => (
            <li key={index}>{note}</li>
          ))}
        </ul>
      ) : null}

      <DsDrawer open={open} onOpenChange={setOpen} title="دسترسی‌ها و امکانات" className="p-4">
        <div className="flex flex-col" dir="rtl">
          <h4 className="mb-1 text-sm font-bold text-slate-800">دسترسی‌ها و امکانات</h4>
          {groups.map((group) => (
            <div key={group.title}>
              <p className="mb-2 mt-3 text-xs font-bold text-slate-500">{group.title}</p>
              <div className="flex flex-wrap gap-2">
                {group.items.map((item) => (
                  <span key={item.key ?? item.name} className={CHIP_CLASS}>
                    {item.icon ? (
                      <img src={item.icon} alt="" loading="lazy" className="h-[17px] w-[17px]" />
                    ) : null}
                    {item.name}
                  </span>
                ))}
              </div>
            </div>
          ))}
          {notes.length > 0 ? (
            <div>
              <p className="mb-1 mt-4 text-xs font-bold text-slate-500">نکات مطب</p>
              <ul className="list-disc pr-4 text-[13px] leading-7 text-slate-700">
                {notes.map((note, index) => (
                  <li key={index}>{note}</li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      </DsDrawer>
    </div>
  );
};

export default OfficeFacilitiesWidget;
